import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useModal } from "../../context/ModalContext";
import PrintQuoteFrame from "../../components/PrintQuoteFrame";
import QuoteCopyBox from "../../components/QuoteCopyBox";
import { useQuoteLimits } from "../../hooks/useQuoteRegion";
import "./OrderPage.css";
import "./OrderPageRenew.css"; // 2026-10-15 리뉴얼 — .ck-order 안에서만 적용
import RegionOnly from "../../components/RegionOnly";
import { trackEvent, trackAdsConversion, ADS_CONV } from "../../utils/gtag";
import { rememberedStoreProductUrl } from "../../utils/storeOrigin";

// 결제·주문은 네이버 스마트스토어에서 한다 (2026-09-08 김현비).
// 여기는 견적서까지만 — 이 상품 페이지에서 견적 금액만큼 수량을 넣어 결제한다.
const SMARTSTORE_URL = "https://smartstore.naver.com/kkuk-studio/products/13622693537";

function OrderPageBody() {
  // Page-specific content/design editor; public visits load only published presentation.
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://os.makerz.co.kr/print-page-editor/runtime.js";
    script.async = true;
    document.body.appendChild(script);
    return () => { window.__makerzPageEditor?.destroy?.(); script.remove(); };
  }, []);
  const { t } = useTranslation();
  const { showAlert } = useModal();
  const history = useHistory();
  // 견적 = print 접수 화면을 그대로 띄운다(PrintQuoteFrame, 2026-10-20 김현비 "동작은 print 와 똑같이, 화면만 다르게").
  //   모양은 iframe 때와 같은 { orderNum, totalPrice, items } — 아래 결제·네이버 복귀 코드를 그대로 쓴다.
  const [makerzQuote, setMakerzQuote] = useState(null);
  // 최소 주문 금액은 print 공통 설정을 따른다 — 문구에 숫자를 박지 않는다
  const { minOrderPrice, shippingBase } = useQuoteLimits();

  // 네이버 스토어 유입자 여부 (localStorage에 저장된 return URL 기준)
  const [naverReturnUrl, setNaverReturnUrl] = useState(null);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("naverReturn") || "null");
      if (stored && stored.url && stored.expiresAt > Date.now()) {
        setNaverReturnUrl(stored.url);
      } else if (stored) {
        // 만료됐으면 정리
        localStorage.removeItem("naverReturn");
      }
    } catch (e) {
      // 파싱 실패 — 무시
    }
  }, []);

  function goNaverStore() {
    if (!makerzQuote || !makerzQuote.orderNum) {
      showAlert(t("order.alerts.noQuote", "견적을 먼저 완료해주세요."));
      return;
    }
    /* 새 창으로 연다 — 견적번호·주문 수량이 이 화면에 남아 있어야 스토어에서 옮겨 적을 수 있다.
       스토어에서 들어온 손님(?return=)이면 그 상품으로, 아니면 쿠키커터 출력 상품 페이지로. */
    // 구글 전환 — 스마트스토어로 이동(= 결제 직전). 결제는 네이버에서 일어나 여기서 못 잡는다.
    trackEvent("begin_checkout", {
      value: Number(makerzQuote?.totalPrice) || 0,
      currency: "KRW",
      transaction_id: makerzQuote?.orderNum,
    });
    trackAdsConversion(ADS_CONV.store, {
      value: Number(makerzQuote?.totalPrice) || 0,
      currency: "KRW",
      transaction_id: makerzQuote?.orderNum,
    });
    try { localStorage.removeItem("naverReturn"); } catch (e) { /* noop */ }
    // 네이버 임베드 복귀 주소 > 블로그 꼬리표(?store=)로 기억한 스토어 > 기본(꾹찍구) (2026-10-21)
    window.open(naverReturnUrl || rememberedStoreProductUrl() || SMARTSTORE_URL, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="order-page ck-order">
      {/* <div className="order-coming-soon-banner">
        {t("order.comingBanner")}
      </div> */}
      <h1 className="order-title">{t("order.title")}</h1>

      <div className="order-form">
        {/* === 이용 안내 === */}
        <section className="order-section order-guide">
          <h2>{t("order.guide.title")}</h2>
          <ol className="order-guide-list">
            <li>{t("order.guide.step1")}</li>
            <li>{t("order.guide.step2")}</li>
            <li>{t("order.guide.step3")}</li>
            <li>{t("order.guide.step4")}</li>
          </ol>
          <p className="order-guide-note">
            {minOrderPrice != null && t("order.guide.minOrder", { price: Number(minOrderPrice).toLocaleString() })}
            {" "}
            {t("order.guide.shipping", "견적 금액에 배송비는 포함되어 있지 않습니다 — 배송비 {{ship}}원 별도 (제주·도서지역 추가).", {
              ship: Number(shippingBase || 0).toLocaleString(),
            })}
          </p>
        </section>

        {/* === 자동 견적 (사내 엔진) === */}
        <section className="order-section order-quote-section">
          {/* 1 견적 받기 · 2 네이버 스토어에서 결제 — 두 단계로 보이게 (2026-10-20 김현비) */}
          <h2 className="order-store-title" style={{ marginBottom: 14 }}><i>1</i>{t("order.quoteTitle", "견적 받기")}</h2>
          <PrintQuoteFrame source="cookistory" onQuote={setMakerzQuote} />
        </section>

        {/* === 결제는 스마트스토어에서 한다 (2026-09-08 김현비) ===
             쿠키스토리에서는 견적서까지만 내고, 결제·주문은 네이버 스마트스토어로 넘긴다.
             그래서 결제 금액 계산칸과 [결제하기] 버튼을 뺐다. */}
        {makerzQuote && (
          <section className="order-section order-store-guide">
            <h2 className="order-store-title"><i>2</i>{t("order.storeTitle", "네이버 스토어에서 결제")}</h2>
            <p>{t("order.storeGuide", "견적서를 확인하신 뒤 네이버 스마트스토어에서 주문·결제해 주세요. 주문 시 견적번호를 남겨 주시면 그대로 제작해 드립니다.")}</p>
            {/* 결제 안내이므로 복사 상자는 결제 버튼 바로 위에 둔다 */}
            <QuoteCopyBox quote={makerzQuote} />
            <button
              type="button"
              className="order-naver-btn"
              onClick={goNaverStore}
            >
              {t("embedOrder.payNaver", "네이버 스토어에서 결제")}
            </button>
            {/* [새 접수하기] 는 위 견적 화면(print)에 이미 있어 여기선 뺀다 (2026-10-21 김현비) */}
          </section>
        )}
      </div>
    </div>
  );
}


// 해외배송을 하지 않으므로 국내 접속에만 보인다 (설계 4).
// 메뉴에서는 이미 빠져 있고, 여기는 주소로 직접 들어온 경우를 위한 것이다.
export default function OrderPage(props) {
  return (
    <RegionOnly>
      <OrderPageBody {...props} />
    </RegionOnly>
  );
}
